/**
 * The one help device on a form field: a dotted "what's this?" beside the label that opens a small
 * card, on hover or on tap.
 *
 * WHY THIS SHAPE (owner, 22 and 24 Sep 2026). Question-mark icons "look a bit ugly", and the
 * in-place expansion the narrative uses — right for prose — pushed a two-column form apart when
 * opened beside a label ("this looks crap"). A form field wants a tooltip: it floats over the page,
 * it moves nothing, and it closes when the pointer leaves. So the trigger is words, not an icon, and
 * the card is the app's own surface — white, a hairline border, a soft shadow — not a black bubble.
 *
 * Hover opens it on a desktop; a tap toggles it on the iPads advisers demo on. It flips above the
 * trigger when there is no room below, and it is rendered through a portal so no `overflow` on an
 * ancestor can clip it.
 */
import React, { useState, useRef, useEffect, useLayoutEffect, useId } from 'react';
import { createPortal } from 'react-dom';

interface InfoIconProps {
    text?: string;
    children?: React.ReactNode;
    /** The trigger's words. "what's this?" by default; a field can name its question instead. */
    label?: string;
    className?: string;
}

const CARD_WIDTH = 300;

const InfoIcon = ({ text, children, label = "what's this?", className = '' }: InfoIconProps) => {
    const [open, setOpen] = useState(false);
    const [pinned, setPinned] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const cardRef = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<{ top: number; left: number; above: boolean }>({ top: 0, left: 0, above: false });
    const id = useId();

    useLayoutEffect(() => {
        if (!open || !triggerRef.current) return;
        const r = triggerRef.current.getBoundingClientRect();
        const cardH = cardRef.current?.offsetHeight ?? 120;
        const vw = window.innerWidth;
        const left = Math.min(Math.max(12, r.left + r.width / 2 - CARD_WIDTH / 2), vw - CARD_WIDTH - 12);
        const roomBelow = window.innerHeight - r.bottom;
        const above = roomBelow < cardH + 16 && r.top > cardH + 16;
        setPos({ top: above ? r.top - 8 : r.bottom + 8, left, above });
    }, [open]);

    // A pinned (tapped-open) card closes on a tap anywhere else, or on Escape.
    useEffect(() => {
        if (!pinned) return undefined;
        const onDown = (e: MouseEvent | TouchEvent) => {
            const t = e.target as Node;
            if (triggerRef.current?.contains(t) || cardRef.current?.contains(t)) return;
            setPinned(false); setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setPinned(false); setOpen(false); } };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('touchstart', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('touchstart', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [pinned]);

    const canPortal = typeof window !== 'undefined' && !!window.document;
    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                aria-describedby={open ? id : undefined}
                aria-expanded={open}
                onMouseEnter={() => setOpen(true)}
                onMouseLeave={() => { if (!pinned) setOpen(false); }}
                onFocus={() => setOpen(true)}
                onBlur={() => { if (!pinned) setOpen(false); }}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); const next = !pinned; setPinned(next); setOpen(next || open); }}
                className={`ml-1.5 inline-flex items-center whitespace-nowrap align-baseline text-[12px] font-semibold text-blue-700 underline decoration-dotted decoration-blue-400 underline-offset-[3px] hover:text-blue-900 hover:decoration-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 rounded-sm ${className}`}
            >
                {label}
            </button>
            {open && canPortal && createPortal(
                <div
                    ref={cardRef}
                    id={id}
                    role="tooltip"
                    style={{ position: 'fixed', top: pos.top, left: pos.left, width: CARD_WIDTH, transform: pos.above ? 'translateY(-100%)' : undefined, zIndex: 10001 }}
                    className="rounded-xl border border-slate-200 bg-white p-3.5 text-[13px] leading-relaxed text-slate-700 shadow-xl shadow-slate-900/10"
                    onMouseEnter={() => setOpen(true)}
                    onMouseLeave={() => { if (!pinned) setOpen(false); }}
                >
                    {typeof text === 'string' ? text : children}
                </div>,
                document.body,
            )}
        </>
    );
};

export default InfoIcon;
